import { cast } from "@flockcover/flock-svc-core-types"
import { useLDFlags } from "@shared/integration/launchdarkly"
import {
  ApiError,
  Feature,
  FeatureCollection,
  GeoJson,
  GeoJsonProperties,
  Geometry,
  LineString,
  LineStringFeatureProperties,
  Map,
  MapContent,
  MapContentRaw,
  PointFeatureProperties,
  Theme,
} from "@shared/primitive-components"
import { LineStringFeaturePropertiesType } from "@shared/primitive-components/src/Map/types"
import { LineString as GeoJsonLineString, Point } from "geojson"
import { useEffect, useMemo } from "react"
import { MapFeatureTooltipContent, MapTitle } from "."
import { generateRouteFromGeoJson } from "../../../utils"
import { HERE_API_KEY } from "../../const"

const generateTooltipContent = (geoJsonFeature: Feature<LineString, LineStringFeatureProperties>): JSX.Element => (
  <MapFeatureTooltipContent geoJsonFeature={geoJsonFeature} />
)

const getColorForSpeedingClassificationFromLineString = (
  geoJsonFeature: Feature<LineString, LineStringFeatureProperties>,
  theme: Theme
) => {
  switch (geoJsonFeature.properties.speedingCategory) {
    case undefined:
      return theme.palette.coal.dark
    case 3:
      return theme.palette.full.red
    case 2:
      return theme.palette.full.orange
    case 1:
      return theme.palette.full.amber
    case 0:
      return theme.palette.full.green
    default:
      return theme.palette.coal.dark
  }
}

const transformGeoJson = (geoJson: FeatureCollection<Geometry, GeoJsonProperties>): GeoJson => {
  const lineStrings = geoJson.features
    .filter(
      (x) =>
        x.geometry.type === `LineString` &&
        x.properties?.speed !== undefined &&
        x.properties?.speedLimit !== undefined &&
        x.properties?.wayId !== undefined
    ) // && x.properties?.distance !== null)
    .map((y) => {
      try {
        return { ...y, properties: cast(LineStringFeaturePropertiesType)(y.properties) } as Feature<
          GeoJsonLineString,
          LineStringFeatureProperties
        >
      } catch (err) {
        console.warn(`⚠️ Failed to cast LineString properties, using as-is:`, y.properties, err)
        return y as any
      }
    })

  const points: Array<Feature<Point, PointFeatureProperties>> = []

  return {
    lineStrings,
    points,
  }
}

type Props = {
  geojsonString: string
  setHasUnreliableTripData: (hasUnreliableTripData: boolean) => void
}

export const TripMapFromString = ({ geojsonString, setHasUnreliableTripData }: Props) => {
  const { showHereMap } = useLDFlags()

  const geojson: GeoJson | null = useMemo(() => {
    try {
      const data = JSON.parse(geojsonString)
      return transformGeoJson(data as FeatureCollection<Geometry, GeoJsonProperties>)
    } catch (error) {
      // console.error(`Failed to parse GeoJSON string:`, error)
      return null
    }
  }, [geojsonString])

  const routeData = useMemo(() => (geojson ? generateRouteFromGeoJson(geojson) : null), [geojson])

  useEffect(() => {
    if (routeData && routeData.hasUnreliableSpeed) setHasUnreliableTripData(true)
  }, [routeData, setHasUnreliableTripData])

  if (!geojson || !routeData) return <ApiError />

  const { route, start, end, bounds } = routeData
  const apiKey = HERE_API_KEY
  const height = showHereMap ? `45vh` : undefined

  return (
    <>
      {showHereMap && <MapTitle title="HERE Map as it will display in Production" />}
      <Map bounds={bounds} height={height}>
        <MapContent
          markers={
            start && end
              ? [
                  { position: start, content: `Start`.toUpperCase() },
                  { position: end, content: `End`.toUpperCase() },
                ]
              : []
          }
          styleFn={getColorForSpeedingClassificationFromLineString}
          generateTooltipContentFn={generateTooltipContent}
          route={route}
          apiKey={apiKey}
        />
      </Map>
      {showHereMap && (
        <>
          <MapTitle title="HERE Map with data points for testing proposes" />
          <Map bounds={bounds} height={height}>
            <MapContentRaw
              styleFn={getColorForSpeedingClassificationFromLineString}
              generateTooltipContentFn={generateTooltipContent}
              route={geojson}
              apiKey={apiKey}
            />
          </Map>
        </>
      )}
    </>
  )
}
